// Utilitaires de formatage centralisés pour Baobab Connect
// Tous les composants doivent utiliser ces fonctions pour assurer la cohérence

import { getVehicleTypeLabel } from '../constants/vehicleTypes';

// === TYPES DE VÉHICULES ===
export const formatVehicleType = (type, typeOther = null) => {
  // Si "autre" est sélectionné et une valeur personnalisée existe
  if (type === 'autre' && typeOther) {
    // Essayer de trouver le label dans la liste des types connus
    const knownLabel = getVehicleTypeLabel(typeOther);
    if (knownLabel && knownLabel !== typeOther) {
      return knownLabel;
    }
    return typeOther; // Afficher la valeur personnalisée directement
  }
  
  // Utiliser le label de la constante centralisée
  return getVehicleTypeLabel(type);
};

// === TYPES D'OPÉRATION ===
const OPERATION_TYPES = {
  transport_marchandises: 'Transport de marchandises',
  transport_marchandise: 'Transport de marchandises',
  livraison_derniere_mile: 'Livraison dernière-mile',
  collecte: 'Collecte / Enlèvement sur site',
  demenagement: 'Déménagement',
  transport_btp: 'Transport de matériel BTP',
  transport_conteneur: 'Transport de conteneur',
  transport_sensible: 'Transport de matériel sensible',
  transport_frigo: 'Transport frigorifique / chaîne du froid',
  transport_interurbain: 'Transport interurbain',
  transport_local: 'Transport local (intra-Abidjan)',
  transport_regional: 'Transport régional (CEDEAO)',
  operation_speciale: 'Opération spéciale',
  local: 'Local',
  national: 'National',
  international: 'International',
  livraison: 'Livraison',
  transport: 'Transport',
  import: 'Import',
  export: 'Export',
  autre: 'Autre'
};

// Libellé lisible pour une valeur libre / inconnue : jamais d'underscore affiché
const prettifyRaw = (raw) => {
  if (raw === null || raw === undefined) return '';
  const text = String(raw)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
};

// Clé normalisée : « Produits Agricoles » → produits_agricoles
const normalizeKey = (raw) => prettifyRaw(raw).toLowerCase().replace(/ /g, '_');

export const formatOperationType = (type, typeOther = null) => {
  const key = normalizeKey(type);
  if ((key === 'autre' || key === 'operation_speciale') && typeOther) {
    return prettifyRaw(typeOther);
  }
  if (!key) return 'Non renseigné';
  return OPERATION_TYPES[key] || prettifyRaw(type);
};

// === TYPES DE MARCHANDISE ===
const MERCHANDISE_TYPES = {
  materiel_btp: 'Matériel BTP (fer, ciment, carreaux, sable…)',
  colis_cartons: 'Colis & cartons',
  mobilier: 'Mobilier / Électroménager',
  refrigere: 'Marchandises réfrigérées',
  alimentaire: 'Produits alimentaires',
  sensible: 'Produits sensibles / fragiles',
  industriel: 'Matériel industriel',
  pieces_detachees: 'Pièces détachées / mécanique',
  consommation: 'Produits de consommation',
  textile: 'Textile / Vêtements',
  produits_chimiques: 'Produits chimiques (non dangereux)',
  bois_materiaux: 'Bois & matériaux de construction',
  electronique: 'Équipements électroniques',
  vehicules: 'Véhicules / Engins',
  meubles_demenagement: 'Meubles / Déménagement',
  produits_agricoles: 'Produits agricoles',
  liquides_vrac: 'Liquides en vrac (citernes)',
  granulats: 'Granulats / Matériaux en vrac',
  agriculture: 'Agriculture',
  automobile: 'Automobile',
  btp: 'BTP & Construction',
  chimie: 'Chimie & Pétrochimie',
  energie: 'Énergie',
  meuble: 'Mobilier',
  pharmaceutique: 'Pharmaceutique',
  metallurgie: 'Métallurgie',
  papier: 'Papier & Carton',
  verre: 'Verre & Céramique',
  plastique: 'Plastique',
  bois: 'Bois',
  dechets: 'Déchets & Recyclage',
  animaux: 'Animaux vivants',
  dangereux: 'Matières dangereuses',
  fragile: 'Marchandises fragiles',
  volumineux: 'Marchandises volumineuses',
  general: 'Marchandises diverses',
  marchandises: 'Marchandises diverses',
  marchandises_diverses: 'Marchandises diverses',
  divers: 'Marchandises diverses',
  produits_alimentaires: 'Produits alimentaires',
  produits_de_consommation: 'Produits de consommation',
  materiaux_construction: 'Matériaux de construction',
  hydrocarbures: 'Hydrocarbures',
  minerais: 'Minerais',
  autre_marchandise: 'Autre',
  autre: 'Autre'
};

export const formatMerchandiseType = (type, typeOther = null) => {
  const key = normalizeKey(type);
  if ((key === 'autre' || key === 'autre_marchandise') && typeOther) {
    return prettifyRaw(typeOther);
  }
  if (!key) return typeOther ? prettifyRaw(typeOther) : 'Non renseignée';
  return MERCHANDISE_TYPES[key] || prettifyRaw(type);
};

// === TYPES DE CONTENEURS ===
const CONTAINER_TYPES = {
  conteneur_20_standard: 'Conteneur 20 pieds Standard',
  conteneur_40_standard: 'Conteneur 40 pieds Standard',
  conteneur_40_hc: 'Conteneur 40 pieds High Cube',
  conteneur_45_hc: 'Conteneur 45 pieds High Cube',
  conteneur_20_frigo: 'Conteneur frigorifique 20 pieds',
  conteneur_40_frigo: 'Conteneur frigorifique 40 pieds',
  conteneur_40_frigo_hc: 'Conteneur frigorifique 40 pieds High Cube',
  conteneur_20_open_top: 'Conteneur Open Top 20 pieds',
  conteneur_40_open_top: 'Conteneur Open Top 40 pieds',
  conteneur_20_flat_rack: 'Conteneur Flat Rack 20 pieds',
  conteneur_40_flat_rack: 'Conteneur Flat Rack 40 pieds',
  conteneur_20_plateforme: 'Conteneur Plateforme 20 pieds',
  conteneur_40_plateforme: 'Conteneur Plateforme 40 pieds',
};

export const formatContainerType = (type) => {
  if (!type) return null;
  return CONTAINER_TYPES[type] || type;
};

// === FORMATAGE DE DATE ===
export const formatDate = (date) => {
  if (!date) return 'Non définie';
  try {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  } catch {
    return date;
  }
};

export const formatDateTime = (date) => {
  if (!date) return 'Non définie';
  try {
    return new Date(date).toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return date;
  }
};

// === FORMATAGE DE PRIX ===
export const formatPrice = (amount, currency = 'XOF') => {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('fr-FR', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount) + ' ' + currency;
};

// === FORMATAGE MONÉTAIRE (compact) ===
export const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '0';
  if (amount >= 1000000000) return `${(amount / 1000000000).toFixed(1)} Mrd`;
  if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)} M`;
  if (amount >= 1000) return `${(amount / 1000).toFixed(0)} K`;
  return new Intl.NumberFormat('fr-FR').format(amount);
};

// === FORMATAGE NOMBRE (simple séparateur milliers) ===
export const formatNumber = (value) => {
  if (value === null || value === undefined) return '0';
  return new Intl.NumberFormat('fr-FR').format(value);
};

// === FORMATAGE DE POIDS ===
export const formatWeight = (weight) => {
  if (!weight) return '-';
  return `${weight} T`;
};

export default {
  formatVehicleType,
  formatOperationType,
  formatMerchandiseType,
  formatDate,
  formatDateTime,
  formatPrice,
  formatCurrency,
  formatNumber,
  formatWeight
};
